import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { StatusBadge } from '~/components/StatusBadge';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  ExternalLink,
  Trash2,
  Loader2,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import type { IFormActivityRow, IFormWithCount, SyncStatus } from '~/types';

interface IFormActivityTableProps {
  rows: IFormActivityRow[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  selectedForm: IFormWithCount | undefined;
  onDelete?: (ids: string[]) => Promise<void>;
  enabledIntegrations?: string[];
}

const PROVIDER_LABELS: Record<string, string> = {
  hubspot: 'HubSpot',
  attio: 'Attio',
  slack: 'Slack',
};

function SyncStatusCell({
  status,
  url,
  errorMessage,
}: {
  status: SyncStatus | null;
  url: string | null;
  errorMessage: string | null;
}) {
  if (status === 'success') {
    if (url) {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1">
          <Badge className="bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Synced
            <ExternalLink className="ml-1 h-3 w-3" />
          </Badge>
        </a>
      );
    }
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        Synced
      </Badge>
    );
  }
  if (status === 'pending') {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
        <Clock className="mr-1 h-3 w-3" />
        Pending
      </Badge>
    );
  }
  if (status === 'failed') {
    const badge = (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
        <AlertCircle className="mr-1 h-3 w-3" />
        Failed
      </Badge>
    );
    if (errorMessage) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="text-xs">{errorMessage}</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    return badge;
  }
  return <span className="text-sm text-muted-foreground">--</span>;
}

function getIntegrationSync(
  row: IFormActivityRow,
  provider: string,
): { status: SyncStatus | null; url: string | null; errorMessage: string | null } {
  if (provider === 'hubspot') {
    return { status: row.hubspotSyncStatus, url: row.hubspotContactUrl, errorMessage: row.hubspotSyncError };
  }
  if (provider === 'attio') {
    return { status: row.attioSyncStatus, url: row.attioPersonUrl, errorMessage: row.attioSyncError };
  }
  if (provider === 'slack') {
    return { status: row.slackSyncStatus, url: null, errorMessage: row.slackSyncError };
  }
  return { status: null, url: null, errorMessage: null };
}

const PAGE_SIZE = 25;

export const FormActivityTable = ({
  rows,
  total,
  page,
  onPageChange,
  selectedForm,
  onDelete,
  enabledIntegrations,
}: IFormActivityTableProps) => {
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Clear selection on page change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page]);

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === rows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.id)));
    }
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  // Get dynamic columns from the selected form's schema
  const dynamicColumns: string[] = selectedForm?.json_schema?.properties
    ? Object.keys(selectedForm.json_schema.properties).slice(0, 4)
    : [];

  /* CRM providers that get a sync-status column */
  const crmProviders = enabledIntegrations ? enabledIntegrations.filter((p) => p in PROVIDER_LABELS) : ['hubspot'];

  const colCount = 5 + dynamicColumns.length + 1 + crmProviders.length + (onDelete ? 1 : 0);

  return (
    <TooltipProvider>
      <div>
        {/* Floating action bar */}
        {selectedIds.size > 0 && onDelete && (
          <div className="flex items-center justify-between rounded-md border bg-muted/50 px-4 py-2 mb-3">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <Button variant="destructive" size="sm" onClick={() => setShowDeleteModal(true)}>
              <Trash2 className="mr-1 h-4 w-4" />
              Delete
            </Button>
          </div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {onDelete && (
                  <TableHead className="w-[40px]">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selectedIds.size === rows.length}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </TableHead>
                )}
                <TableHead className="w-[160px]">Timestamp</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[120px]">Qualification</TableHead>
                <TableHead className="w-[200px]">Email</TableHead>
                {dynamicColumns.map((col) => (
                  <TableHead key={col} className="capitalize">
                    {col.replace(/_/g, ' ')}
                  </TableHead>
                ))}
                <TableHead className="w-[100px]">Scheduler</TableHead>
                {crmProviders.map((provider) => (
                  <TableHead key={provider} className="w-[100px]">
                    {PROVIDER_LABELS[provider] ?? provider}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="h-24 text-center text-muted-foreground">
                    No submissions found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} data-state={selectedIds.has(row.id) ? 'selected' : undefined}>
                    {onDelete && (
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleRow(row.id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(row.createdAt), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell>
                      {row.qualification ? (
                        <StatusBadge status="Qualified" />
                      ) : (
                        <span className="text-sm text-muted-foreground">--</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{row.email || '--'}</TableCell>
                    {dynamicColumns.map((col) => (
                      <TableCell key={col} className="text-sm max-w-[200px] truncate">
                        {row.formData[col] != null ? String(row.formData[col]) : '--'}
                      </TableCell>
                    ))}
                    <TableCell>
                      {row.calendlyEventBooked ? (
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                          <Calendar className="mr-1 h-3 w-3" />
                          Booked
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">--</span>
                      )}
                    </TableCell>
                    {crmProviders.map((provider) => {
                      const sync = getIntegrationSync(row, provider);
                      return (
                        <TableCell key={provider}>
                          <SyncStatusCell status={sync.status} url={sync.url} errorMessage={sync.errorMessage} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Page {page} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2">Delete Submissions</h3>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete {selectedIds.size} submission{selectedIds.size !== 1 ? 's' : ''}? This
                  action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end gap-2 px-6 pb-6">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
                  {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
